import React from 'react';
import { navigate } from 'gatsby';

// ── Общий Nocturne-набор: иконка, тона, пилюли, кнопки.
// До этого файла блок копипастился в ~12 страниц и в два фасада раздела
// (components/comm/ui.js, components/ab/ui.js). Значения — токены из
// src/styles/tokens.css; голые rgba/hex там, где var() не годится
// (color-mix не используем — старые браузеры склада).
//
// ВАЖНО: любое изменение здесь двигает пиксели сразу на всех страницах.
// Если странице нужен другой размер/тон — локальная константа на странице,
// а не правка общего значения.

// Иконка Phosphor (@phosphor-icons/web подключён глобально в components/Layout.js).
// style — для страниц, у которых своё выравнивание (lineHeight/verticalAlign).
export const Ico = ({ name, size = 14, color, style }) => (
  <i className={`ph ${name}`} style={{ fontSize: size, color, verticalAlign: '-1px', ...style }} />
);

// Внутренняя ссылка БЕЗ полной перезагрузки приложения. Обычный <a href="/page">
// — это браузерная навигация: Gatsby грузит бандл заново, и все keep-alive вкладки
// (см. components/tabs/TabsKeepAlive.js) теряют состояние — загруженная таблица
// репрайсера, правки, фильтры. Роутер Gatsby переключает страницу без перезагрузки.
// Ctrl/Cmd/Shift и средняя кнопка не перехватываются — «открыть в новой вкладке» работает.
// Использовать так: <Box component="a" {...spaLink(href)} sx={…}>…</Box>
export const spaLink = (to) => ({
  href: to,
  onClick: (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  },
});

// Плейсхолдер фото: neutral-300 55%
export const PHOTO_BG = 'rgba(207,211,229,.55)';

// Клик по строке таблицы = её кнопка-стрелка. Мимо: контролы и ссылки внутри строки, выделение текста
// и клики в порталах (меню/поповеры всплывают по дереву React, но в DOM строки их нет).
export const isRowToggleClick = (e) => e.currentTarget.contains(e.target)
  && !e.target.closest('a,button,input,textarea,select,label,[role="button"],[role="combobox"],[role="checkbox"],[role="option"],[contenteditable="true"],[data-noexpand],.MuiChip-root,.MuiInputBase-root')
  && !String((typeof window !== 'undefined' && window.getSelection && window.getSelection()) || '');

// ── Тона. Два набора, и они НЕ взаимозаменяемы:
// TONE — приглушённые токены, для второстепенной семантики (подписи, ячейки таблиц).
export const TONE = {
  ok: { fg: 'var(--ok-fg)', bg: 'var(--ok-bg)' },
  warn: { fg: 'var(--warn-fg)', bg: 'var(--warn-bg)' },
  err: { fg: 'var(--err-fg)', bg: 'var(--err-bg)' },
  accent: { fg: 'var(--color-accent-700)', bg: 'var(--color-accent-100)' },
  muted: { fg: 'var(--color-neutral-500)', bg: 'var(--color-neutral-200)' },
};

// SIGNAL — ЯРКИЕ статусные цвета (фидбек заказчика 25.08): бейджи и светофоры,
// которые должны читаться через всю таблицу. Приглушённые --ok/--err на них тонут.
export const SIGNAL = {
  ok: { fg: '#2e7d32', bg: 'rgba(46,125,50,.12)' },
  err: { fg: '#d32f2f', bg: 'rgba(211,47,47,.10)' },
  warn: { fg: 'var(--warn-fg)', bg: 'var(--warn-bg)' },
  accent: { fg: 'var(--color-accent-700)', bg: 'var(--color-accent-100)' },
  muted: { fg: 'var(--color-neutral-600)', bg: 'var(--color-neutral-200)' },
};

// ── Пилюли. Две высоты одного силуэта:
// pillSx — метка (11px, обычный вес): фирма, артикул, счётчик.
export const pillSx = { height: 18, fontSize: 11, borderRadius: 'var(--radius-sm)', '& .MuiChip-label': { px: 0.75 } };
// statusPillSx — статус (10.5px, 600): тон задаёт tonePillSx.
export const statusPillSx = {
  height: 18, fontSize: 10.5, fontWeight: 600, borderRadius: 'var(--radius-sm)',
  '& .MuiChip-label': { px: 0.75 },
};
// Тон передаётся объектом {fg,bg} — из SIGNAL или TONE.
export const tonePillSx = (t) => ({ ...statusPillSx, color: t.fg, borderColor: t.fg, bgcolor: t.bg });

// ── Кнопки. Тоже две высоты, и путать их нельзя:
// smallBtnSx — чиповая, внутри карточек (уведомления, чаты, отзывы).
export const smallBtnSx = { borderRadius: '7px', px: 1.25, py: 0.25, fontSize: 11.5, minWidth: 0, lineHeight: 1.7 };
export const accentBtnSx = { ...smallBtnSx, color: 'var(--color-accent-700)', borderColor: 'var(--color-accent-600)' };
export const neutralBtnSx = { ...smallBtnSx, color: 'var(--color-neutral-700)', borderColor: 'var(--color-neutral-300)' };
// Тон передаётся КЛЮЧОМ TONE ('ok'|'warn'|'err'|'accent'|'muted').
export const toneBtnSx = (t) => ({
  ...smallBtnSx, color: TONE[t].fg, borderColor: TONE[t].fg,
  '&:hover': { borderColor: TONE[t].fg, backgroundColor: TONE[t].bg },
});

// btnSx — обычная страничная кнопка-действие (шапки страниц, диалоги).
export const btnSx = { borderRadius: 'var(--radius-md)', fontSize: 12.5, px: 1.75, py: 0.5 };
export const disabledBtnSx = { color: 'var(--color-neutral-400)', borderColor: 'var(--color-neutral-300)' };
export const accentPageBtnSx = {
  ...btnSx,
  color: 'var(--color-accent-700)',
  borderColor: 'var(--color-accent-600)',
  '&:hover': { borderColor: 'var(--color-accent-700)', backgroundColor: 'var(--color-accent-100)' },
};
export const neutralPageBtnSx = {
  ...btnSx,
  color: 'var(--color-neutral-700)',
  borderColor: 'var(--color-neutral-300)',
  '&:hover': { borderColor: 'var(--color-neutral-400)', backgroundColor: 'var(--color-neutral-200)' },
};

// ── Типографика карточек
export const titleSx = { fontSize: 13.5, fontWeight: 600 };
export const bodySx = { fontSize: 12.5, color: 'var(--color-neutral-700)' };
export const mutedSx = { fontSize: 11, color: 'var(--color-neutral-500)' };
export const emptySx = { fontSize: 12.5, color: 'var(--color-neutral-500)', py: 4, textAlign: 'center' };
